import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';

// Service configuration with health endpoints
const SERVICES = [
  // Core Services
  { name: 'Orbit Gateway', url: 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app', category: 'Core', critical: true },
  // Genesis Router deprecated 2026-02-16: absorbed into ai-service/genesis module
  // { name: 'Genesis Router', url: 'https://genesis-router-buzbhnk2ha-uc.a.run.app', category: 'Core', critical: true },
  { name: 'AI Service', url: 'https://ai-service-buzbhnk2ha-uc.a.run.app', category: 'Core', critical: true },
  { name: 'Manus Service', url: 'https://manus-service-buzbhnk2ha-uc.a.run.app', category: 'Core', critical: true },
  
  // Memory & Storage
  { name: 'Memory Agent', url: 'https://memory-agent-buzbhnk2ha-uc.a.run.app', category: 'Memory', critical: true },
  { name: 'Vector Store', url: 'https://vector-store-buzbhnk2ha-uc.a.run.app', category: 'Memory', critical: false },
  { name: 'Storage Service', url: 'https://storage-service-buzbhnk2ha-uc.a.run.app', category: 'Memory', critical: false },
  
  // Agent Services
  // Note: Nexus Agent merged into genesis-router (2026-01-31)
  { name: 'Aegis Agent', url: 'https://aegis-agent-buzbhnk2ha-uc.a.run.app', category: 'Agents', critical: false },
  { name: 'Wave Coordinator', url: 'https://wave-coordinator-buzbhnk2ha-uc.a.run.app', category: 'Agents', critical: false },
  
  // Tools & Integrations
  { name: 'Browser Service', url: 'https://browser-service-buzbhnk2ha-uc.a.run.app', category: 'Tools', critical: false },
  { name: 'Sandbox Service', url: 'https://sandbox-service-buzbhnk2ha-uc.a.run.app', category: 'Tools', critical: false },
  { name: 'Tools Service', url: 'https://tools-service-buzbhnk2ha-uc.a.run.app', category: 'Tools', critical: false },
  // Note: calendar-service replaced by Trust-Drip + conduit-service (2026-02-14)
  // Note: voice-service replaced by Gemini Live API (2026-02-12)
  
  // Infrastructure
  { name: 'Monitor Service', url: 'https://monitor-service-buzbhnk2ha-uc.a.run.app', category: 'Infrastructure', critical: false },
  { name: 'Notify Service', url: 'https://notify-service-buzbhnk2ha-uc.a.run.app', category: 'Infrastructure', critical: false },
  // Note: websocket-service replaced by Gemini Live API (2026-02-12)
  { name: 'App Registry', url: 'https://app-registry-buzbhnk2ha-uc.a.run.app', category: 'Infrastructure', critical: false },
  { name: 'App Launcher', url: 'https://app-launcher-buzbhnk2ha-uc.a.run.app', category: 'Infrastructure', critical: false },
  
  // Specialized
  { name: 'Conduit Service', url: 'https://conduit-service-buzbhnk2ha-uc.a.run.app', category: 'Specialized', critical: false },
  { name: 'Synapse Refinery', url: 'https://synapse-refinery-buzbhnk2ha-uc.a.run.app', category: 'Specialized', critical: false },
  { name: 'SuperShelley Hub', url: 'https://supershelley-hub-buzbhnk2ha-uc.a.run.app', category: 'Specialized', critical: false },
  { name: 'Identity Vault', url: 'https://identity-vault-buzbhnk2ha-uc.a.run.app', category: 'Specialized', critical: true },
];

interface ServiceStatus {
  name: string;
  url: string;
  category: string;
  critical: boolean;
  status: 'online' | 'offline' | 'checking' | 'unknown';
  responseTime?: number;
  lastChecked?: Date;
  error?: string;
  version?: string;
}

const MonitorScreen: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [services, setServices] = useState<ServiceStatus[]>(
    SERVICES.map(s => ({ ...s, status: 'unknown' as const }))
  );
  const [isChecking, setIsChecking] = useState(false);
  const [lastFullCheck, setLastFullCheck] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const checkService = useCallback(async (service: typeof SERVICES[0]): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${service.url}/health`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });
      
      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;
      
      if (response.ok) {
        let version: string | undefined;
        try {
          const data = await response.json();
          version = data.version || data.agent || data.service;
        } catch {
          // Response might not be JSON
        }
        
        return {
          ...service,
          status: 'online',
          responseTime,
          lastChecked: new Date(),
          version,
        };
      } else {
        return {
          ...service,
          status: 'offline',
          responseTime,
          lastChecked: new Date(),
          error: `HTTP ${response.status}`,
        };
      }
    } catch (error) {
      return {
        ...service,
        status: 'offline',
        responseTime: Date.now() - startTime,
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, []);

  const checkAllServices = useCallback(async () => {
    setIsChecking(true);
    
    // Set all to checking
    setServices(prev => prev.map(s => ({ ...s, status: 'checking' as const })));
    
    // Check all services in parallel
    const results = await Promise.all(
      SERVICES.map(service => checkService(service))
    );
    
    setServices(results);
    setLastFullCheck(new Date());
    setIsChecking(false);
  }, [checkService]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(checkAllServices, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, checkAllServices]);

  // Initial check on mount
  useEffect(() => {
    checkAllServices();
  }, [checkAllServices]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-cyan-400 text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const onlineCount = services.filter(s => s.status === 'online').length;
  const offlineCount = services.filter(s => s.status === 'offline').length;
  const criticalOffline = services.filter(s => s.critical && s.status === 'offline');
  
  const categories = [...new Set(SERVICES.map(s => s.category))];

  const getStatusColor = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'offline': return 'bg-red-500';
      case 'checking': return 'bg-yellow-500 animate-pulse';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'online': return '✓';
      case 'offline': return '✗';
      case 'checking': return '⟳';
      default: return '?';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-cyan-400">Service Monitor</h1>
            <p className="text-gray-400 mt-1">
              Real-time health status of SuperJarvis services
            </p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-400">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-600 bg-gray-800 text-cyan-500 focus:ring-cyan-500"
              />
              Auto-refresh (30s)
            </label>
            <button
              onClick={checkAllServices}
              disabled={isChecking}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 rounded-lg text-white font-medium transition-colors"
            >
              {isChecking ? 'Checking...' : 'Refresh All'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="text-gray-400 text-sm">Total Services</div>
            <div className="text-3xl font-bold text-white">{services.length}</div>
          </div>
          <div className="bg-gray-900 border border-green-900 rounded-xl p-4">
            <div className="text-green-400 text-sm">Online</div>
            <div className="text-3xl font-bold text-green-400">{onlineCount}</div>
          </div>
          <div className="bg-gray-900 border border-red-900 rounded-xl p-4">
            <div className="text-red-400 text-sm">Offline</div>
            <div className="text-3xl font-bold text-red-400">{offlineCount}</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="text-gray-400 text-sm">Last Check</div>
            <div className="text-lg font-medium text-white">
              {lastFullCheck ? lastFullCheck.toLocaleTimeString() : 'Never'}
            </div>
          </div>
        </div>

        {/* Critical Alert */}
        {criticalOffline.length > 0 && (
          <div className="bg-red-900/30 border border-red-500 rounded-xl p-4 mb-8">
            <div className="flex items-center gap-2 text-red-400 font-bold mb-2">
              <span className="text-xl">⚠️</span>
              Critical Services Offline
            </div>
            <div className="text-red-300">
              {criticalOffline.map(s => s.name).join(', ')}
            </div>
          </div>
        )}

        {/* Services by Category */}
        {categories.map(category => (
          <div key={category} className="mb-8">
            <h2 className="text-xl font-semibold text-gray-300 mb-4">{category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services
                .filter(s => s.category === category)
                .map(service => (
                  <div
                    key={service.name}
                    className={`bg-gray-900 border rounded-xl p-4 transition-all ${
                      service.critical ? 'border-cyan-800' : 'border-gray-800'
                    } ${service.status === 'offline' ? 'border-red-800' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status)}`} />
                        <span className="font-medium">{service.name}</span>
                        {service.critical && (
                          <span className="text-xs bg-cyan-900 text-cyan-300 px-2 py-0.5 rounded">
                            Critical
                          </span>
                        )}
                      </div>
                      <span className="text-lg">{getStatusIcon(service.status)}</span>
                    </div>
                    
                    <div className="text-sm text-gray-400 space-y-1">
                      {service.responseTime !== undefined && (
                        <div>Response: {service.responseTime}ms</div>
                      )}
                      {service.version && (
                        <div>Version: {service.version}</div>
                      )}
                      {service.error && (
                        <div className="text-red-400">Error: {service.error}</div>
                      )}
                      {service.lastChecked && (
                        <div>Checked: {service.lastChecked.toLocaleTimeString()}</div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8">
          SuperJarvis Service Monitor • {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

export default MonitorScreen;
